import { Copy, SmartLink } from '@stokr/components-library'
import { useStoPageActions } from 'context/StoPageActionsContext'
import { useStoProject } from 'hooks/sto/useStoProject'
import { toOnboardingLink } from 'shared/utils/app-urls'
import { SectionSkeleton } from 'components/sto/Section'
import { useEligibilityCheckPanel } from 'hooks/sto/useEligibility'
import { isRedemptionCompleted } from 'hooks/sto/redemptionCompleted'
import { MODAL_TYPES, type MarketType, type ModalType } from 'shared/domain/projectEligibility'
import { FieldLabel, FooterAction, FooterCard, FooterDetail, FooterMessage, FooterText } from './KeyActions.styles'
import { useStoTrack } from 'hooks/sto/useStoTracking'

const STEP_MODAL_CY: Partial<Record<ModalType, string>> = {
  [MODAL_TYPES.PRIVATE_INVESTOR]: 'private-investor',
  [MODAL_TYPES.EU_PROFESSIONAL_INVESTOR]: 'professional-investor',
}

/**
 * "Eligibility check" panel. Driven entirely by the ported eligibility logic, so a
 * signed-in visitor never sees a sign-up prompt — they see what they still have to do,
 * or nothing to do at all.
 *
 * When there is genuinely no action (already eligible, under review, product
 * unavailable) no button renders, rather than a button that goes nowhere.
 *
 * Uses the shared library `Button` rather than a local pill so this matches every other
 * primary action on the platform.
 */
export const EligibilityCheckFooter = ({
  layout = 'card',
  market,
}: {
  layout?: 'card' | 'header'
  market?: MarketType
}) => {
  const state = useEligibilityCheckPanel(market)
  const { openLoginModal, openStepModal } = useStoPageActions()
  const { slug, project } = useStoProject()
  const trackSto = useStoTrack()
  const placement = layout === 'header' ? 'sticky_header' : 'eligibility_check'

  // Hidden when there's nothing to say (fully eligible, see useEligibilityCheckPanel). A fully
  // redeemed product keeps only the signed-out log-in prompt: there are no steps left to take.
  const isSignInPrompt = state.status === 'ready' && Boolean(state.data.cta?.opensLoginModal)
  if (state.status === 'hidden' || (isRedemptionCompleted(project) && !isSignInPrompt)) return null

  // Until eligibility can be trusted: never flash "steps left" at a just-signed-in visitor.
  if (state.status === 'loading') {
    return (
      <FooterCard $layout={layout} aria-busy="true">
        <FooterText>
          <FieldLabel>
            <Copy id="stoPage.keyActions.eligibility.label" />
          </FieldLabel>
          <SectionSkeleton rows={1} rowHeight={24} />
        </FooterText>
      </FooterCard>
    )
  }

  const { message, detail, cta, stepsRemaining } = state.data

  return (
    <FooterCard $layout={layout}>
      <FooterText>
        <FieldLabel>
          <Copy id="stoPage.keyActions.eligibility.label" />
        </FieldLabel>
        <FooterMessage>{message}</FooterMessage>
        {/* The sticky header keeps to one line: the detail only fits the card. */}
        {detail && layout !== 'header' && <FooterDetail>{detail}</FooterDetail>}
      </FooterText>

      {cta &&
        (cta.opensLoginModal ? (
          // Signed-out: opens the login modal in place, same as HowToInvest's sign-in
          // step, rather than navigating the visitor away to sign up.
          <FooterAction
            type="button"
            data-cy={`sto-${placement.replace('_', '-')}-login`}
            onClick={() => {
              trackSto('sto_login_clicked', { location: placement })
              openLoginModal()
            }}>
            {cta.label}
          </FooterAction>
        ) : cta.opensModal ? (
          // Completed in place, as HowToInvest's private-investor step.
          <FooterAction
            type="button"
            data-cy={`sto-${placement.replace('_', '-')}-${STEP_MODAL_CY[cta.opensModal] ?? 'step-modal'}`}
            onClick={() => {
              trackSto('sto_eligibility_cta_clicked', { location: placement, steps_remaining: stepsRemaining })
              if (cta.opensModal) openStepModal(cta.opensModal)
            }}>
            {cta.label}
          </FooterAction>
        ) : (
          cta.link && (
            // SmartLink for the href case: these onboarding URLs are an in-app path in
            // some environments and absolute in others, and a bare <a> would full-reload
            // the SPA.
            // Sign-up-flow sends the visitor back here (KeyActions) once the step is done.
            <FooterAction
              as={SmartLink}
              {...toOnboardingLink(cta.link, slug ? `/${slug}#key-actions` : undefined)}
              data-cy={`sto-${placement.replace('_', '-')}-complete-steps`}
              onClick={() =>
                trackSto('sto_eligibility_cta_clicked', { location: placement, steps_remaining: stepsRemaining })
              }>
              {cta.label}
            </FooterAction>
          )
        ))}
    </FooterCard>
  )
}
